<template>
    <div class="relative">
      <div v-if="loading" class="h-10rem">
        <div class="imab-logo-animated" style="left: 30%; top: 10%;">
          <div><span class="I">I</span><span class="M">M</span><span class="A">A</span><span class="B">B</span></div>
        </div>
      </div>
      <div v-else>
        <section v-if="hasMarkedRosters" class="pb-3 text-sm text-yellow-600 font-bold" aria-live="polite">
          <p class="m-0 p-0">You cannot edit a marked {{ effectiveViewType }}. Please go back and select another one.</p>
        </section>
        <section v-if="hasMixedRosterTypes" class="pb-3 text-sm text-yellow-600 font-bold" aria-live="polite">
          <p class="m-0 p-0">Shift and leave records cannot be edited together. Please select records of the same type.</p>
        </section>
        <section v-if="missingRosterIds.length" class="pb-3 text-sm text-yellow-600 font-bold" aria-live="polite">
          <p class="m-0 p-0">Could not load roster IDs: {{ missingRosterIds.join(', ') }}.</p>
        </section>
        <DataTable :value="reactiveRosters" aria-label="Roster Table">
          <Column field="employee_name" header="Employee" class="text-600 pl-3">
            <template #body="slotProps">
              <div class="flex items-center gap-2 pl-1">
                <div>
                  <p class="block font-bold pb-1 m-0 p-0">
                    {{ slotProps.data.employee_name }}
                    <span v-if="slotProps.data.roster_marked" class="text-xs text-yellow-600 pl-2" aria-label="Marked">[MARKED]</span>
                  </p>
                  <p class="block text-xs text-300 m-0 p-0 pb-1">{{ slotProps.data.employee_company_name }}</p>
                  <p class="text-xs text-300 mr-2 m-0 p-0">
                    <span class="mr-2">@{{ slotProps.data.employee_id }}</span>
                    <span class="mr-2">{{ slotProps.data.employee_status }}</span>
                    <span class="mr-2">{{ slotProps.data.batch_name }}</span>
                  </p>
                </div>
              </div>
            </template>
          </Column>
          <Column field="shift_type" header="Type" class="text-600">
            <template #body="slotProps">
              <div class="items-center gap-2">
                <p class="block pb-1 m-0 p-0">
                  <Tag severity="info" :class="
                                  {'bg-yellow-800': slotProps.data.shift_type==='LEAVE', 
                                  'bg-cyan-800': slotProps.data.shift_type==='COVER', 
                                  'surface-300': slotProps.data.shift_type==='SHIFT'}" style="letter-spacing: 2px;">
                                  {{ slotProps.data.shift_type }}
                  </Tag>
                </p>
                <p class="block text-xs text-300 m-0 p-0">ID #{{ slotProps.data.roster_id }}</p>
              </div>
            </template>
          </Column>
          <Column v-if="effectiveViewType === 'leaves'" field="shift_reason" header="Leave Reason" class="text-600">
            <template #body="slotProps">
              <div class="items-center gap-2">
                <Dropdown
                  v-model="slotProps.data.shift_reason"
                  :options="SHIFT_REASONS"
                  optionLabel="label"
                  optionValue="value"
                  :disabled="isSubmittingRoster || isSubmittingLeave || isDeleting || isEditingBlocked"
                  class="w-full"
                  placeholder="Select Reason"
                />
              </div>
            </template>
          </Column>
          <Column field="roster_day" header="Day" class="text-600">
            <template #body="slotProps">
              <div class="items-center gap-2">
                <p class="block pb-1 m-0 p-0">{{ slotProps.data.roster_day }}</p>
                <p class="block text-xs text-300 m-0 p-0">{{ slotProps.data.roster_month }}</p>
              </div>
            </template>
          </Column>
          <Column field="roster_start_date" header="Date" class="text-600">
            <template #body="slotProps">
              <NativeDateInput
                v-model="slotProps.data.roster_start_date"
                :disabled="isSubmittingRoster || isSubmittingLeave || isDeleting || isEditingBlocked"
                @date-select="updateMonthAndDay(slotProps.data)"
                aria-label="Start Date"
              />
            </template>
          </Column>
          <Column field="roster_start_time" header="Start" class="text-600">
            <template #body="slotProps">
              <div class="flex">
                <div>
                  <NativeTimeInput
                    v-model="slotProps.data.roster_start_time"
                    :disabled="isSubmittingRoster || isSubmittingLeave || isDeleting || isEditingBlocked"
                    aria-label="Start Time"
                  />
                </div>
                <div v-if="!isEditingBlocked && reactiveRosters && reactiveRosters.length > 1">
                  <Button icon="pi pi-arrow-down" @click="handleUpdateStartTimesBelow(slotProps.data)" :disabled="slotProps.data.day_off" class="ml-2 w-2rem surface-200 hover:surface-400" />
                </div>
              </div>
            </template>
          </Column>
          <Column field="roster_end_time" header="End" class="text-600">
            <template #body="slotProps">
              <div class="flex">
                <div>
                  <NativeTimeInput
                    v-model="slotProps.data.roster_end_time"
                    :disabled="isSubmittingRoster || isSubmittingLeave || isDeleting || isEditingBlocked"
                    aria-label="End Time"
                    />
                </div>
                <div v-if="!isEditingBlocked && reactiveRosters && reactiveRosters.length > 1">
                  <Button icon="pi pi-arrow-down" @click="handleUpdateEndTimesBelow(slotProps.data)" :disabled="slotProps.data.day_off" class="ml-2 w-2rem surface-200 hover:surface-400" />
                </div>
              </div>
            </template>
          </Column>
          <Column field="duration" header="Duration" class="text-600 text-center">
            <template #body="slotProps">
              <Tag severity="info" :class="{
                                    'bg-red-600 block text-900': calculateDuration(slotProps.data.roster_start_time, slotProps.data.roster_end_time) <= 0,
                                    'surface-200 block text-900': calculateDuration(slotProps.data.roster_start_time, slotProps.data.roster_end_time) > 0
                                    }">
                                    {{ calculateDuration(slotProps.data.roster_start_time, slotProps.data.roster_end_time) }}</Tag>
            </template>
          </Column>
        </DataTable>
        <footer class="flex overflow-hidden mt-3">
          <div class="flex-shrink-0 flex align-items-center justify-content-left mr-2">
            <Button :label="`Delete ${effectiveViewType}`" icon="pi pi-trash" @click="submitDelete" :loading="isDeleting" :disabled="isSubmittingRoster || isSubmittingLeave || isEditingBlocked" type="submit" class="w-full bg-red-700 hover:bg-red-500" aria-label="Delete selected rosters" />
          </div>
          <div class="flex-shrink-1 flex-grow-1 flex align-items-center justify-content-left mr-2">
            <Button v-if="hasLeaveShifts" 
                    label="Mark as Shift" 
                    icon="pi pi-check-circle" 
                    @click="submitUpdate('SHIFT')" 
                    :loading="isSubmittingShift" 
                    :disabled="isSubmittingRoster || isSubmittingLeave || isDeleting || isEditingBlocked"
                    type="submit" 
                    class="bg-cyan-800 hover:bg-cyan-500" 
                    aria-label="Mark selected leave as shift" />
            <Button v-else 
                    label="Mark Leave" 
                    icon="pi pi-minus-circle" 
                    @click="submitUpdate('LEAVE')" 
                    :loading="isSubmittingLeave" 
                    :disabled="isSubmittingRoster || isSubmittingLeave || isDeleting || isEditingBlocked"
                    type="submit" 
                    class="bg-yellow-800 hover:bg-yellow-500" 
                    aria-label="Mark selected shifts as leave" />
          </div>
          <div class="flex-shrink-0 flex align-items-center justify-content-end mr-2">
            <Button label="Cancel" class="w-full surface-300 hover:surface-500" @click="handleBack" :disabled="isSubmittingRoster || isSubmittingLeave || isDeleting" aria-label="Cancel and go back" />
          </div>
          <div class="flex-shrink-0 flex align-items-center justify-content-end">
            <Button :label="`Save ${effectiveViewType}`" severity="primary" type="submit" @click="submitUpdate" :loading="isSubmittingRoster" :disabled="isDeleting || isEditingBlocked" class="w-full hover:bg-primary-300" aria-label="Edit selected rosters" />
          </div>
        </footer>
      </div>
    </div>
</template>

<script setup>
import { onMounted, watch, defineProps, defineEmits, ref, computed } from 'vue';
import { useRosterBulk } from '@/composables/api/useRosterBulk';
import { useRosterBulkUpdate } from '@/composables/api/useRosterBulkUpdate';
import { useRosterBulkDelete } from '@/composables/api/useRosterBulkDelete';
import { useUtils } from '@/composables/useUtils';
import { useToast } from 'primevue/usetoast';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import NativeDateInput from '@/components/NativeDateInput.vue';
import NativeTimeInput from '@/components/NativeTimeInput.vue';
import { useLondonUnixTime } from '@/composables/time/useLondonTime';

// Emits & Props
const emit = defineEmits(['updateSuccess', 'handleBack', 'loaded']);
const props = defineProps({
  selectedRosterIds: {
    type: Array,
    required: true
  },
  viewType: {
    type: String,
    default: null,
    validator: value => value === null || ['roster', 'leaves'].includes(value)
  }
});

// Api Calls
const { bulkRosters, loading, error, fetchBulkRosters } = useRosterBulk();
const { isSubmittingRoster, isSubmittingLeave, isSubmittingShift, updateRosters } = useRosterBulkUpdate();
const { isDeleting, bulkDeleteRoster } = useRosterBulkDelete();
const { 
  updateStartTimesBelow, 
  updateEndTimesBelow, 
  parseTime, 
  updateMonthAndDay, 
  getHoursAndMinutes, 
  getDateComponents,
  calculateDuration,
  generateBatchName
} = useUtils();
const { getLondonDate } = useLondonUnixTime();

// Constants
const SHIFT_REASONS = [
  { label: 'Undefined', value: 'UNDEFINED' },
  { label: 'Death in Family', value: 'DEATH_IN_FAMILY_LEAVE' },
  { label: 'Casual Leave', value: 'CASUAL_LEAVE' },
  { label: 'Emergency Leave', value: 'EMERGENCY_LEAVE' },
  { label: 'Wedding Leave', value: 'WEDDING_LEAVE' },
  { label: 'Birthday Leave', value: 'BIRTHDAY_LEAVE' },
  { label: 'Uninformed Leave', value: 'UNINFORMED_LEAVE' },
  { label: 'Road Accident', value: 'ROAD_ACCIDENT_LEAVE' },
  { label: 'National Holiday', value: 'NATIONAL_HOLIDAY_LEAVE' },
  { label: 'Sick Leave', value: 'SICK_LEAVE' },
  { label: 'Put on Hold', value: 'PUT_ONHOLD_LEAVE' }
];

// Refs
const reactiveRosters = ref([]);
const loadedViewType = ref('roster');
const missingRosterIds = ref([]);
const toast = useToast();

// Computed
const hasMarkedRosters = computed(() => {
  return reactiveRosters.value.some(roster => roster.roster_marked === true);
});

// Add a new computed property to check if there are any LEAVE shifts
const hasLeaveShifts = computed(() => {
  return reactiveRosters.value.some(roster => roster.shift_type === 'LEAVE');
});

const hasMixedRosterTypes = computed(() => {
  const rosterTypes = new Set(reactiveRosters.value.map(
    roster => roster.shift_type === 'LEAVE' ? 'leaves' : 'roster'
  ));
  return rosterTypes.size > 1;
});

const effectiveViewType = computed(() => props.viewType || loadedViewType.value);

const isEditingBlocked = computed(() => (
  hasMarkedRosters.value
  || hasMixedRosterTypes.value
  || missingRosterIds.value.length > 0
  || reactiveRosters.value.length === 0
));

// Functions
const handleUpdateStartTimesBelow = (currentRow) => {
  updateStartTimesBelow(reactiveRosters.value, currentRow, (updatedRosters) => {
    reactiveRosters.value = updatedRosters;
  });
};

const handleUpdateEndTimesBelow = (currentRow) => {
  updateEndTimesBelow(reactiveRosters.value, currentRow, (updatedRosters) => {
    reactiveRosters.value = updatedRosters;
  });
};

const handleBack = async () => {
    emit('handleBack');
}

const loadRosterData = async () => {
  try {
    const requestSucceeded = await fetchBulkRosters(props.selectedRosterIds);
    if (!requestSucceeded) {
      reactiveRosters.value = [];
      missingRosterIds.value = [...props.selectedRosterIds];
      return;
    }

    reactiveRosters.value = bulkRosters.value.map(roster => {
      const londonDate = getLondonDate(new Date(roster.roster_start_date));
      return {
        ...roster,
        roster_start_date: londonDate,
        roster_start_time: parseTime(roster.roster_start_time),
        roster_end_time: parseTime(roster.roster_end_time),
        batch_name: generateBatchName(londonDate)
      };
    });

    const loadedRosterIds = new Set(reactiveRosters.value.map(roster => Number(roster.roster_id)));
    missingRosterIds.value = props.selectedRosterIds.filter(
      rosterId => !loadedRosterIds.has(rosterId)
    );

    const loadedTypes = new Set(reactiveRosters.value.map(
      roster => roster.shift_type === 'LEAVE' ? 'leaves' : 'roster'
    ));
    loadedViewType.value = loadedTypes.size === 1
      ? Array.from(loadedTypes)[0]
      : 'roster';

    emit('loaded', {
      viewType: loadedViewType.value,
      rosterCount: reactiveRosters.value.length,
      hasMixedRosterTypes: hasMixedRosterTypes.value,
      missingRosterIds: [...missingRosterIds.value]
    });
  } catch (error) {
    console.error('Error fetching roster data:', error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load roster data', life: 12000 });
  }
};

// Add watcher for roster_start_date changes to update batch_name
watch(reactiveRosters, (newRosters) => {
  newRosters.forEach(roster => {
    roster.batch_name = generateBatchName(roster.roster_start_date);
  });
}, { deep: true });

// Bulk Edit Roster
const submitUpdate = async (action = '') => {
    const formattedRosters = reactiveRosters.value
      .map(roster => {
          const [startHours, startMinutes] = getHoursAndMinutes(roster.roster_start_time);
          const [endHours, endMinutes] = getHoursAndMinutes(roster.roster_end_time);
          const dateComponents = getDateComponents(roster.roster_start_date);

          if (!dateComponents) return null;

          const [year, month, day] = dateComponents;
          
          let rosterStart = Date.UTC(year, month, day, startHours, startMinutes);
          let rosterEnd = Date.UTC(year, month, day, endHours, endMinutes);
          
          if (rosterEnd < rosterStart) {
              rosterEnd = Date.UTC(year, month, day + 1, endHours, endMinutes);
          }

          return {
              rosterId: roster.roster_id,
              employeeId: roster.employee_id,
              rosterMonth: roster.roster_month,
              rosterShift: action === 'LEAVE' ? 'LEAVE' : (action === 'SHIFT' ? 'SHIFT' : roster.shift_type),
              rosterDay: roster.roster_day,
              rosterStart,
              rosterEnd,
              batchName: roster.batch_name,
              shiftReason: roster.shift_reason || 'UNDEFINED'
          };
      })
      .filter(Boolean);

    if (formattedRosters.length === 0) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'No valid roster data to update', life: 12000 });
      if (action === 'LEAVE') isSubmittingLeave.value = false;
      else if (action === 'SHIFT') isSubmittingShift.value = false;
      else isSubmittingRoster.value = false;
      return;
    }

    const result = await updateRosters(formattedRosters, action);

    if (result.success) {
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: result.message,
          life: 4000,
          group: 'br'
        });
        emit('updateSuccess', { viewType: effectiveViewType.value });
    } else {
        toast.add({
          severity: result.severity || 'error',
          summary: 'Error',
          detail: result.message,
          life: 12000,
          group: 'br'
        });
    }
};

// Bulk Delete Roster
const submitDelete = async () => {
  const rosterIds = reactiveRosters.value.map(roster => roster.roster_id);
  const employeeIds = reactiveRosters.value.map(roster => roster.employee_id);

  const result = await bulkDeleteRoster(rosterIds, employeeIds);

  if (result.success) {
  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: result.message,
    life: 6000,
    group: 'br'
  });
  emit('updateSuccess', { viewType: effectiveViewType.value });
  } else {
      toast.add({
        severity: result.severity || 'error',
        summary: 'Error',
        detail: result.message,
        life: 12000,
        group: 'br'
      });
  }
};


// Watch for errors from useRosterBulk
watch(error, (newError) => {
  if (newError) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to fetch roster data: ${newError}`,
      life: 12000,
      group: 'br'
    });
  }
});

onMounted(loadRosterData);
watch(() => props.selectedRosterIds, loadRosterData);
</script>
